import { useEffect, useState } from 'react';
import { useDropzone } from 'react-dropzone';
import styled from '@emotion/styled';
import { uiTheme as t } from 'Components/ui';

export interface ImageDropzoneLabels {
  drop: string;
  hint: string;
  remove: string;
  tooLarge: string;
  invalidType: string;
  /** Nombre accesible del preview (`alt`). */
  alt: string;
}

interface ImageDropzoneProps {
  /** Archivo nuevo elegido por el usuario, o null. */
  file: File | null;
  onFile: (file: File | null) => void;
  /** URL firmada de la imagen ya guardada (edición); se muestra si no hay archivo nuevo. */
  currentImageUrl: string | null;
  labels: ImageDropzoneLabels;
}

const MAX_SIZE_BYTES = 5 * 1024 * 1024;
const ACCEPT = 'image/jpeg,image/png,image/webp,image/gif';

const Zone = styled.div<{ active: boolean }>(({ active }) => ({
  border: `1.5px dashed ${active ? t.color.primary : t.color.inputBorder}`,
  borderRadius: t.radius.sm,
  background: active ? '#f2faf5' : t.color.inputBg,
  padding: '18px 16px',
  textAlign: 'center',
  cursor: 'pointer',
  fontFamily: t.font,
  fontSize: 13.5,
  color: t.color.text,
  transition: 'border-color 0.15s ease, background 0.15s ease',
}));

const Hint = styled.div({
  marginTop: 4,
  fontSize: 12,
  color: t.color.muted,
});

const Preview = styled.img({
  display: 'block',
  width: '100%',
  maxHeight: 180,
  objectFit: 'cover',
  borderRadius: t.radius.sm,
  marginBottom: 10,
});

const RemoveButton = styled.button({
  border: 'none',
  background: 'transparent',
  cursor: 'pointer',
  fontFamily: t.font,
  fontSize: 12.5,
  color: t.color.danger,
  padding: 0,
  marginBottom: 10,
});

const ErrorText = styled.div({
  marginTop: 6,
  fontFamily: t.font,
  fontSize: 12,
  color: t.color.danger,
});

/**
 * Dropzone de imagen de la noticia (patrón `EditCourseInformation/ImageUpload`):
 * jpg/png/webp/gif, máximo 5 MB, un solo archivo. El preview local sale de
 * `URL.createObjectURL`; en edición, mientras no haya archivo nuevo, se muestra
 * la imagen guardada (URL firmada). El archivo vive fuera del schema zod.
 */
const ImageDropzone = ({ file, onFile, currentImageUrl, labels }: ImageDropzoneProps) => {
  const [localPreview, setLocalPreview] = useState<string | null>(null);

  const { getRootProps, getInputProps, isDragActive, fileRejections } = useDropzone({
    accept: ACCEPT,
    maxFiles: 1,
    multiple: false,
    maxSize: MAX_SIZE_BYTES,
    onDrop: accepted => {
      if (accepted[0]) onFile(accepted[0]);
    },
  });

  // Preview local del archivo nuevo, liberando el object URL anterior.
  useEffect(() => {
    if (!file || typeof URL.createObjectURL !== 'function') {
      setLocalPreview(null);
      return undefined;
    }
    const url = URL.createObjectURL(file);
    setLocalPreview(url);
    return () => URL.revokeObjectURL(url);
  }, [file]);

  const rejectionMessage = (() => {
    const firstError = fileRejections[0]?.errors?.[0];
    if (!firstError) return null;
    if (firstError.code === 'file-too-large') return labels.tooLarge;
    if (firstError.code === 'file-invalid-type') return labels.invalidType;
    return firstError.message;
  })();

  const preview = file ? localPreview : currentImageUrl;

  return (
    <div>
      {preview ? <Preview src={preview} alt={labels.alt} /> : null}
      {file ? (
        <RemoveButton type="button" onClick={() => onFile(null)}>
          {labels.remove}
        </RemoveButton>
      ) : null}
      <Zone {...getRootProps()} active={isDragActive}>
        <input {...getInputProps()} />
        <div>{labels.drop}</div>
        <Hint>{labels.hint}</Hint>
      </Zone>
      {rejectionMessage ? <ErrorText>{rejectionMessage}</ErrorText> : null}
    </div>
  );
};

export default ImageDropzone;
